// TODO: vitest component test (pages are covered by Playwright e2e)
import { useEffect } from 'react'
import { useParams, useNavigate } from 'react-router'
import { useTranslation } from 'react-i18next'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { PageHeader, Breadcrumbs } from '@/components/layout'
import { Button, Card, FormActions, Input, Select, Skeleton } from '@/components/ui'
import {
  useFixedAsset,
  useCreateFixedAsset,
  useUpdateFixedAsset,
  useGlAccounts,
} from '@/hooks/useAccounting'
import { useProperties } from '@/hooks/useProperties'

const fixedAssetFormSchema = z.object({
  name: z.string().min(1),
  description: z.string().optional(),
  acquisition_date: z.string().min(1),
  acquisition_cost: z.coerce.number().positive(),
  salvage_value: z.coerce.number().min(0).default(0),
  useful_life_months: z.coerce.number().int().positive(),
  depreciation_method: z.enum(['straight_line', 'declining_balance']),
  gl_account_id: z.string().optional(),
  depreciation_gl_account_id: z.string().optional(),
  property_id: z.string().optional(),
})

type FixedAssetFormData = z.infer<typeof fixedAssetFormSchema>

export function FixedAssetFormPage() {
  const { t } = useTranslation('accounting')
  const { t: tc } = useTranslation('common')
  const { id } = useParams<{ id: string }>()
  const isEditing = !!id
  const assetId = Number(id)
  const navigate = useNavigate()

  const { data: existingAsset, isLoading: isLoadingAsset } = useFixedAsset(
    isEditing ? assetId : 0
  )
  const createAsset = useCreateFixedAsset()
  const updateAsset = useUpdateFixedAsset()

  const { data: glAccountsData } = useGlAccounts({ active: true })
  const { data: propertiesData } = useProperties()

  const glAccounts = glAccountsData?.data || []
  const properties = propertiesData?.data || []

  const glAccountOptions = [
    { value: '', label: t('filters.none') },
    ...glAccounts.map((a) => ({ value: String(a.id), label: `${a.code} - ${a.name}` })),
  ]

  const propertyOptions = [
    { value: '', label: t('filters.none') },
    ...properties.map((p) => ({ value: String(p.id), label: p.name })),
  ]

  const depreciationMethodOptions = [
    { value: 'straight_line', label: t('depreciation_methods.straight_line') },
    { value: 'declining_balance', label: t('depreciation_methods.declining_balance') },
  ]

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<FixedAssetFormData>({
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    resolver: zodResolver(fixedAssetFormSchema) as any,
    defaultValues: {
      depreciation_method: 'straight_line',
      salvage_value: 0,
      useful_life_months: 60,
    },
  })

  useEffect(() => {
    if (isEditing && existingAsset) {
      reset({
        name: existingAsset.name,
        description: existingAsset.description ?? '',
        acquisition_date: existingAsset.acquisition_date,
        acquisition_cost: existingAsset.acquisition_cost,
        salvage_value: existingAsset.salvage_value,
        useful_life_months: existingAsset.useful_life_months,
        depreciation_method: existingAsset.depreciation_method,
        gl_account_id: existingAsset.gl_account_id ? String(existingAsset.gl_account_id) : '',
        depreciation_gl_account_id: existingAsset.depreciation_gl_account_id
          ? String(existingAsset.depreciation_gl_account_id)
          : '',
        property_id: existingAsset.property_id ? String(existingAsset.property_id) : '',
      })
    }
  }, [isEditing, existingAsset, reset])

  const onSubmit = async (formData: FixedAssetFormData) => {
    const input = {
      name: formData.name,
      description: formData.description || undefined,
      acquisition_date: formData.acquisition_date,
      acquisition_cost: Number(formData.acquisition_cost),
      salvage_value: Number(formData.salvage_value) || 0,
      useful_life_months: Number(formData.useful_life_months),
      depreciation_method: formData.depreciation_method,
      gl_account_id: formData.gl_account_id ? Number(formData.gl_account_id) : null,
      depreciation_gl_account_id: formData.depreciation_gl_account_id
        ? Number(formData.depreciation_gl_account_id)
        : null,
      property_id: formData.property_id ? Number(formData.property_id) : null,
    }

    if (isEditing) {
      await updateAsset.mutateAsync({ id: assetId, ...input })
      navigate(`/accounting/fixed-assets/${assetId}`)
    } else {
      const created = await createAsset.mutateAsync(input)
      navigate(`/accounting/fixed-assets/${created.id}`)
    }
  }

  if (isEditing && isLoadingAsset) {
    return (
      <div className="space-y-6">
        <Breadcrumbs
          items={[
            { label: t('fixed_assets.title'), href: '/accounting/fixed-assets' },
            { label: '...' },
          ]}
        />
        <Card>
          <div className="space-y-4">
            {[...Array(5)].map((_, i) => (
              <Skeleton key={i} className="h-10" />
            ))}
          </div>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <Breadcrumbs
        items={[
          { label: t('fixed_assets.title'), href: '/accounting/fixed-assets' },
          ...(isEditing
            ? [
                { label: existingAsset?.name ?? '...', href: `/accounting/fixed-assets/${assetId}` },
                { label: tc('actions.edit') },
              ]
            : [{ label: t('fixed_assets.new_asset') }]),
        ]}
      />

      <PageHeader
        title={isEditing ? `${existingAsset?.name ?? ''} — ${tc('actions.edit')}` : t('fixed_assets.new_asset')}
        description={t('fixed_assets.description')}
      />

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <Card>
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label={t('labels.name')}
                placeholder={t('placeholders.asset_name')}
                error={errors.name?.message}
                {...register('name')}
              />
              <Select
                label={t('labels.property')}
                options={propertyOptions}
                {...register('property_id')}
              />
            </div>

            <Input
              label={t('labels.description')}
              placeholder={t('placeholders.description')}
              {...register('description')}
            />

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Input
                label={t('labels.acquisition_date')}
                type="date"
                error={errors.acquisition_date?.message}
                {...register('acquisition_date')}
              />
              <Input
                label={t('labels.acquisition_cost')}
                type="number"
                step="0.01"
                min="0"
                error={errors.acquisition_cost?.message}
                {...register('acquisition_cost')}
              />
              <Input
                label={t('labels.salvage_value')}
                type="number"
                step="0.01"
                min="0"
                {...register('salvage_value')}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label={t('labels.useful_life')}
                type="number"
                min="1"
                error={errors.useful_life_months?.message}
                {...register('useful_life_months')}
              />
              <Select
                label={t('labels.depreciation_method')}
                options={depreciationMethodOptions}
                {...register('depreciation_method')}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Select
                label={t('labels.gl_account')}
                options={glAccountOptions}
                {...register('gl_account_id')}
              />
              <Select
                label={t('labels.depreciation_account')}
                options={glAccountOptions}
                {...register('depreciation_gl_account_id')}
              />
            </div>
          </div>
        </Card>

        <FormActions>
          <Button
            type="button"
            variant="ghost"
            onClick={() =>
              navigate(
                isEditing
                  ? `/accounting/fixed-assets/${assetId}`
                  : '/accounting/fixed-assets'
              )
            }
          >
            {tc('actions.cancel')}
          </Button>
          <Button
            type="submit"
            loading={isSubmitting || createAsset.isPending || updateAsset.isPending}
          >
            {tc('actions.save')}
          </Button>
        </FormActions>
      </form>
    </div>
  )
}
